import {
  column,
  dateColumn,
  FilterDefs,
  GridColumn,
  GridTableLayout,
  HasIdAndName,
  HeaderAction,
  multiFilter,
  PageHeaderLayout,
  RowStyles,
  simpleDataRows,
  SimpleHeaderAndData,
  useGridTableLayoutState,
} from "@homebound/beam";
import { useCallback, useMemo } from "react";
import { createExportCsvUrl, createGlobalOptionsFormUrl } from "src/RouteUrls";
import { GlobalOptionsFilter, GlobalOptionStatus } from "src/generated/graphqlTypes";
import { useDocumentTitle } from "src/hooks/useDocumentTitle";
import {
  GlobalOptionsPageV2_GlobalOptionFragment,
  GlobalOptionsPageV2Query,
  useGlobalOptionsPageV2MetadataQuery,
  useGlobalOptionsPageV2Query,
} from "src/routes/libraries/global-options-v2/GlobalOptionsPageV2.generated";
import { GlobalOptionsTagColumn } from "src/routes/libraries/global-options/components/GlobalOptionsTagColumn";
import { queryResult } from "src/utils";
import { formatWithShortYear } from "src/utils/dates";
import { openNewTab } from "src/utils/window";
import { Temporal } from "temporal-polyfill";

export function GlobalOptionsPageV2() {
  const query = useGlobalOptionsPageV2MetadataQuery();
  useDocumentTitle("Global Options");
  return queryResult(query, {
    data: ({ globalOptionTypes }) => <GlobalOptionsPageV2View globalOptionTypes={globalOptionTypes} />,
  });
}

export type GlobalOptionsPageV2ViewProps = { globalOptionTypes: HasIdAndName[] };

export const globalOptionStatuses = [
  { name: "Available", status: GlobalOptionStatus.Active },
  { name: "Pending Approval", status: GlobalOptionStatus.PendingApproval },
  { name: "Discontinued", status: GlobalOptionStatus.Archived },
];

export function GlobalOptionsPageV2View(props: GlobalOptionsPageV2ViewProps) {
  const { globalOptionTypes } = props;
  const filterDefs: FilterDefs<GlobalOptionsFilter> = useMemo(() => {
    const type = multiFilter({
      label: "Option type",
      options: globalOptionTypes,
      getOptionLabel: ({ name }) => name,
      getOptionValue: ({ id }) => id,
    });
    const status = multiFilter({
      label: "Status",
      options: globalOptionStatuses,
      getOptionLabel: ({ name }) => name,
      getOptionValue: ({ status }) => status,
    });
    return { type, status };
  }, [globalOptionTypes]);

  const layoutState = useGridTableLayoutState<GlobalOptionsFilter>({
    persistedFilter: { filterDefs, storageKey: "globalOptionsFilter" },
    search: "client",
  });
  const query = useGlobalOptionsPageV2Query({
    variables: { filter: { ...layoutState.filter, isCatalogV2: true, version: [2] } },
  });
  const rowStyles = useMemo(() => createRowStyles(), []);
  const createRows = useCallback(
    (data: GlobalOptionsPageV2Query | undefined) => simpleDataRows(data?.globalOptions ?? []),
    [],
  );
  const headerActions: HeaderAction[] = useMemo(
    () => [
      { label: "Export", variant: "secondary", onClick: () => openNewTab(`${createExportCsvUrl("globalOptions")}`) },
      { label: "Create", variant: "primary", onClick: createGlobalOptionsFormUrl() },
    ],
    [],
  );

  return (
    <PageHeaderLayout pageHeader={{ title: "Options", actions: headerActions }}>
      <GridTableLayout
        hideEditColumns
        layoutState={layoutState}
        tableProps={{
          query,
          createRows,
          columns,
          rowStyles,
          sorting: { on: "client" },
          as: "virtual",
          emptyFallback: "No global options found",
        }}
      />
    </PageHeaderLayout>
  );
}

type Row = SimpleHeaderAndData<GlobalOptionsPageV2_GlobalOptionFragment>;

const columns: GridColumn<Row>[] = [
  column<Row>({ header: "Code", data: ({ code }) => code, mw: "110px" }),
  column<Row>({ header: "Internal name", data: ({ name }) => name, w: 2, mw: "240px" }),
  column<Row>({
    header: "Group",
    // For single option groups, don't needlessly repeat the name
    data: ({ group }) => (group.isSingleOptionGroup ? "" : group.name),
    w: 1.5,
    mw: "140px",
  }),
  column<Row>({ header: "Option type", data: ({ type }) => type.name, mw: "160px" }),
  column<Row>({
    header: "Location",
    data: ({ locations }) => locations.map(({ name }) => name).join(", "),
    w: 2,
    mw: "200px",
  }),
  column<Row>({
    header: "Tag",
    data: (option) => <GlobalOptionsTagColumn option={option} />,
    wrapAction: false,
    clientSideSort: false,
    w: 2,
    mw: "200px",
  }),
  column<Row>({ header: "Status", data: ({ statusDetail }) => statusDetail.name, mw: "150px" }),
  dateColumn<Row>({
    header: "Active date",
    data: ({ activeDate, archivedDate }) => ({ content: activeDateToArchivedCell(activeDate, archivedDate) }),
    mw: "180px",
    clientSideSort: false,
  }),
];

function activeDateToArchivedCell(
  activeDate: Temporal.PlainDate | null | undefined,
  archivedDate: Temporal.PlainDate | null | undefined,
) {
  if (activeDate && archivedDate) {
    return `${formatWithShortYear(activeDate)} - ${formatWithShortYear(archivedDate)}`;
  }
  return activeDate ? `${formatWithShortYear(activeDate)} - present` : `none`;
}

function createRowStyles(): RowStyles<Row> {
  return { header: {}, data: { rowLink: ({ id }) => createGlobalOptionsFormUrl(id) } };
}
